import { AlertTriangle, ArrowDownRight, ArrowUpRight, CheckCircle2, Info, XCircle } from "lucide-react";
import { useEffect, useState, type ComponentProps, type CSSProperties, type ReactNode } from "react";
import { cx, initials, type Tone } from "../util";
import { Skeleton } from "./Spinner";

// ── Card ────────────────────────────────────────────────────────────────────

export interface CardProps extends Omit<ComponentProps<"section">, "title"> {
  title?: ReactNode;
  /** Small line under the title. */
  subtitle?: ReactNode;
  /** Right side of the header row. */
  actions?: ReactNode;
  footer?: ReactNode;
  /** Padding of the body. `none` for edge-to-edge tables. */
  padding?: "none" | "sm" | "md" | "lg";
  /** A thin IG-gradient line across the top: use for the ONE card that matters most on a page. */
  accent?: boolean;
  /** Hover lift for clickable cards. */
  interactive?: boolean;
}

export function Card({ title, subtitle, actions, footer, padding = "md", accent, interactive, className, children, ...rest }: CardProps) {
  const hasHeader = title || subtitle || actions;
  return (
    <section {...rest} className={cx("gf-card", accent && "gf-card--accent", interactive && "gf-card--interactive", className)}>
      {hasHeader ? (
        <header className="gf-card__head">
          <div className="gf-card__titles">
            {title ? <h3 className="gf-card__title">{title}</h3> : null}
            {subtitle ? <p className="gf-card__subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="gf-card__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cx("gf-card__body", `gf-card__body--${padding}`)}>{children}</div>
      {footer ? <footer className="gf-card__foot">{footer}</footer> : null}
    </section>
  );
}

// ── Badge ───────────────────────────────────────────────────────────────────

export interface BadgeProps extends ComponentProps<"span"> {
  tone?: Tone | "gradient";
  /** Any CSS colour (e.g. a CRM stage colour). Overrides `tone`. */
  color?: string;
  /** Leading status dot. */
  dot?: boolean;
  size?: "sm" | "md";
  icon?: ReactNode;
}

export function Badge({ tone = "neutral", color, dot, size = "md", icon, className, style, children, ...rest }: BadgeProps) {
  const custom = color ? ({ "--gf-badge-color": color } as CSSProperties) : undefined;
  return (
    <span
      {...rest}
      style={{ ...custom, ...style }}
      className={cx("gf-badge", `gf-badge--${size}`, color ? "gf-badge--custom" : `gf-badge--${tone}`, className)}
    >
      {dot ? <span className="gf-badge__dot" aria-hidden="true" /> : null}
      {icon ? <span className="gf-badge__icon">{icon}</span> : null}
      {children}
    </span>
  );
}

// ── Avatar ──────────────────────────────────────────────────────────────────

export interface AvatarProps {
  /** Image URL. Instagram CDN URLs work (no referrer is sent). */
  src?: string | null;
  /** Used for alt text and the initials fallback. */
  name?: string | null;
  size?: number | "xs" | "sm" | "md" | "lg" | "xl";
  /** Instagram-style gradient ring. */
  ring?: boolean;
  /** Square-ish (for brands) instead of round. */
  shape?: "circle" | "rounded";
  className?: string;
  title?: string;
}

const AVATAR_SIZES = { xs: 20, sm: 28, md: 36, lg: 48, xl: 72 } as const;

export function Avatar({ src, name, size = "md", ring, shape = "circle", className, title }: AvatarProps) {
  const px = typeof size === "number" ? size : AVATAR_SIZES[size];
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const showImg = !!src && !failed;
  const style = { width: px, height: px, fontSize: Math.max(10, Math.round(px * 0.38)) } as CSSProperties;
  const inner = (
    <span className={cx("gf-avatar", `gf-avatar--${shape}`, !ring && className)} style={style} title={title}>
      {showImg ? (
        <img
          className="gf-avatar__img"
          src={src ?? undefined}
          alt={name ? name : ""}
          referrerPolicy="no-referrer"
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="gf-avatar__initials" aria-label={name ?? undefined} role={name ? "img" : undefined}>
          {initials(name)}
        </span>
      )}
    </span>
  );
  if (!ring) return inner;
  return (
    <span className={cx("gf-avatar-ring", `gf-avatar-ring--${shape}`, className)} style={{ padding: px >= 48 ? 3 : 2 }}>
      {inner}
    </span>
  );
}

// ── Stat (KPI tile) ─────────────────────────────────────────────────────────

export interface StatProps {
  label: ReactNode;
  value: ReactNode;
  /** Change vs. the previous period. Number → coloured ▲/▼ with `deltaSuffix`. */
  delta?: number | null;
  deltaSuffix?: string;
  /** Text after the delta, e.g. "vs last week". */
  deltaLabel?: ReactNode;
  /** When down is good (e.g. "blocks"). */
  invertDelta?: boolean;
  hint?: ReactNode;
  icon?: ReactNode;
  loading?: boolean;
  /** Paint the number with the IG gradient. One per row, max. */
  highlight?: boolean;
  className?: string;
}

export function Stat({ label, value, delta, deltaSuffix = "%", deltaLabel, invertDelta, hint, icon, loading, highlight, className }: StatProps) {
  const hasDelta = typeof delta === "number" && Number.isFinite(delta);
  const good = hasDelta ? (invertDelta ? delta! < 0 : delta! > 0) : false;
  const flat = hasDelta && delta === 0;
  return (
    <div className={cx("gf-stat", className)}>
      <div className="gf-stat__top">
        <span className="gf-stat__label">{label}</span>
        {icon ? <span className="gf-stat__icon">{icon}</span> : null}
      </div>
      {loading ? (
        <Skeleton height={34} width="60%" style={{ marginTop: 6 }} />
      ) : (
        <div className={cx("gf-stat__value", highlight && "gf-stat__value--highlight")}>{value}</div>
      )}
      {hasDelta || deltaLabel || hint ? (
        <div className="gf-stat__foot">
          {hasDelta ? (
            <span className={cx("gf-stat__delta", flat ? "gf-stat__delta--flat" : good ? "gf-stat__delta--good" : "gf-stat__delta--bad")}>
              {!flat ? delta! > 0 ? <ArrowUpRight size={14} aria-hidden="true" /> : <ArrowDownRight size={14} aria-hidden="true" /> : null}
              {delta! > 0 ? "+" : ""}
              {delta}
              {deltaSuffix}
            </span>
          ) : null}
          {deltaLabel ? <span className="gf-stat__deltalabel">{deltaLabel}</span> : null}
          {hint && !hasDelta && !deltaLabel ? <span className="gf-stat__hint">{hint}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

// ── PageHeader ──────────────────────────────────────────────────────────────

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Slot above the title (a back link / breadcrumb). */
  back?: ReactNode;
  /** Slot under the description (tabs, filters). */
  children?: ReactNode;
  className?: string;
}

export function PageHeader({ title, description, actions, back, children, className }: PageHeaderProps) {
  return (
    <header className={cx("gf-pagehead", className)}>
      {back ? <div className="gf-pagehead__back">{back}</div> : null}
      <div className="gf-pagehead__row">
        <div className="gf-pagehead__text">
          <h1 className="gf-pagehead__title">{title}</h1>
          {description ? <p className="gf-pagehead__desc">{description}</p> : null}
        </div>
        {actions ? <div className="gf-pagehead__actions">{actions}</div> : null}
      </div>
      {children ? <div className="gf-pagehead__extra">{children}</div> : null}
    </header>
  );
}

// ── ProgressBar ─────────────────────────────────────────────────────────────

export interface ProgressBarProps {
  value: number;
  max?: number;
  /** Text on the left of the header row (e.g. "Follows today"). */
  label?: ReactNode;
  /** Right side of the header row. Defaults to "value / max" when `showValue`. */
  valueLabel?: ReactNode;
  showValue?: boolean;
  /** Force a tone; otherwise gradient, turning warn ≥ 85% and danger at 100%. */
  tone?: Tone | "gradient";
  size?: "sm" | "md";
  className?: string;
}

export function ProgressBar({ value, max = 100, label, valueLabel, showValue = true, tone, size = "md", className }: ProgressBarProps) {
  const safeMax = max > 0 ? max : 1;
  const pct = Math.max(0, Math.min(100, (value / safeMax) * 100));
  const auto: Tone | "gradient" = pct >= 100 ? "danger" : pct >= 85 ? "warn" : "gradient";
  const t = tone ?? auto;
  const right = valueLabel ?? (showValue ? `${value.toLocaleString()} / ${max.toLocaleString()}` : null);
  return (
    <div className={cx("gf-progress", `gf-progress--${size}`, className)}>
      {label || right ? (
        <div className="gf-progress__head">
          {label ? <span className="gf-progress__label">{label}</span> : <span />}
          {right ? <span className="gf-progress__value">{right}</span> : null}
        </div>
      ) : null}
      <div
        className="gf-progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-label={typeof label === "string" ? label : undefined}
      >
        <span
          className={cx("gf-progress__fill", `gf-progress__fill--${t}`)}
          // The gradient is sized to the whole track so a short bar shows the warm end, not a squashed rainbow.
          style={{ width: `${pct}%`, backgroundSize: t === "gradient" && pct > 0 ? `${10000 / pct}% 100%` : undefined }}
        />
      </div>
    </div>
  );
}

// ── EmptyState ──────────────────────────────────────────────────────────────

export interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Buttons / links. */
  action?: ReactNode;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function EmptyState({ icon, title, description, action, size = "md", className }: EmptyStateProps) {
  return (
    <div className={cx("gf-empty", `gf-empty--${size}`, className)}>
      {icon ? <div className="gf-empty__icon">{icon}</div> : null}
      <h3 className="gf-empty__title">{title}</h3>
      {description ? <p className="gf-empty__desc">{description}</p> : null}
      {action ? <div className="gf-empty__action">{action}</div> : null}
    </div>
  );
}

// ── Callout (inline notice) ─────────────────────────────────────────────────

export interface CalloutProps {
  tone?: Exclude<Tone, "accent" | "neutral"> | "neutral";
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode | false;
  action?: ReactNode;
  className?: string;
}

const CALLOUT_ICONS = {
  info: <Info size={18} />,
  success: <CheckCircle2 size={18} />,
  warn: <AlertTriangle size={18} />,
  danger: <XCircle size={18} />,
  neutral: <Info size={18} />,
};

export function Callout({ tone = "info", title, children, icon, action, className }: CalloutProps) {
  return (
    <div className={cx("gf-callout", `gf-callout--${tone}`, className)} role={tone === "danger" ? "alert" : undefined}>
      {icon !== false ? <span className="gf-callout__icon">{icon ?? CALLOUT_ICONS[tone]}</span> : null}
      <div className="gf-callout__body">
        {title ? <p className="gf-callout__title">{title}</p> : null}
        {children ? <div className="gf-callout__text">{children}</div> : null}
      </div>
      {action ? <div className="gf-callout__action">{action}</div> : null}
    </div>
  );
}

/** Visually hidden, still read by screen readers. */
export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="gf-sr-only">{children}</span>;
}
